import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';

/** Section divider above each group of rows (My Reports / Shared With Me / Other). */
export const GroupHeaderRow: React.FC<{ label: string; count: number; colSpan: number }> = ({
  label,
  count,
  colSpan,
}) => {
  const theme = useTheme();
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          bgcolor: theme.palette.v4.tableEven,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'v4.border',
          py: 0.75,
          px: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>({count})</Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export type MyReportsStatusVariant = 'loading' | 'error' | 'no-results' | 'no-reports';

export interface MyReportsStatusRowProps {
  variant: MyReportsStatusVariant;
  colSpan: number;
}

/**
 * The table body when there are no rows to show. Four distinct states, because
 * "you own no reports", "your filters matched nothing", "still loading" and
 * "the request failed" are four different things the user needs to be told
 * apart — the page previously showed the filter message for all of them.
 *
 * Rendered in-table (rather than as a card in place of the table) so the column
 * headers and the filter bar stay on screen: when a filter is what emptied the
 * list, the user needs to see the filter that did it. Matches RegionGrid and
 * ReplenishmentConfigGrid.
 */
export const MyReportsStatusRow: React.FC<MyReportsStatusRowProps> = ({ variant, colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
      {variant === 'loading' ? (
        <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
      ) : (
        <StatusMessage variant={variant} />
      )}
    </TableCell>
  </TableRow>
);

const StatusMessage: React.FC<{ variant: Exclude<MyReportsStatusVariant, 'loading'> }> = ({
  variant,
}) => {
  if (variant === 'error') {
    return (
      <Typography variant="body2" sx={{ color: 'error.main' }}>
        Could not load your reports. Refresh to try again.
      </Typography>
    );
  }

  if (variant === 'no-results') {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No reports match your filters.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        You don&apos;t have any saved reports yet.
      </Typography>
      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
        Save a report from any list page and it will appear here.
      </Typography>
    </Box>
  );
};
